import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, AlertCircle, Loader2, Link2, Building2 } from 'lucide-react';
import api, { Client, GoogleOAuthCallbackResponse, ManagedAccount } from '../services/api';

type ServiceType = 'google_ads' | 'gbp' | 'gsc' | 'facebook_ads';

interface ParsedState {
  uuid: string;
  service: ServiceType;
}

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const processedRef = useRef(false);

  const [step, setStep] = useState<'exchanging' | 'selecting' | 'linking' | 'success' | 'error'>('exchanging');
  const [error, setError] = useState<string | null>(null);
  const [serviceType, setServiceType] = useState<ServiceType | null>(null);
  const [oauthResponse, setOauthResponse] = useState<GoogleOAuthCallbackResponse | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedSubAccount, setSelectedSubAccount] = useState<string>('');

  useEffect(() => {
    // Remove Facebook's #_=_ fragment if present
    if (window.location.hash === '#_=_') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    // Only process once to avoid issues with re-renders
    if (processedRef.current) {
      return;
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(`Authorization failed: ${errorParam}`);
      setStep('error');
      processedRef.current = true;
      return;
    }

    if (!code || !state) {
      setError('Authorization code or state not found');
      setStep('error');
      processedRef.current = true;
      return;
    }

    // Mark as processed before async operations
    processedRef.current = true;

    // Parse state to get service type
    try {
      const parsedState: ParsedState = JSON.parse(state);
      setServiceType(parsedState.service);
      exchangeCode(code, state);
    } catch {
      setError('Invalid state parameter');
      setStep('error');
    }
  }, [searchParams]);

  const exchangeCode = async (code: string, state: string) => {
    try {
      setStep('exchanging');

      // Parse state to determine which OAuth callback to use
      let parsedService: ServiceType | null = null;
      try {
        const parsed = JSON.parse(state);
        parsedService = parsed.service;
      } catch {
        // State might not be JSON for some providers
      }

      // Use Facebook-specific callback if service is facebook_ads
      const response = parsedService === 'facebook_ads'
        ? await api.facebookAdsOAuthCallback(code, state)
        : await api.googleOAuthCallback(code, state);

      // For GSC, check sites instead of accounts
      if (response.service === 'gsc') {
        if (!response.sites || response.sites.length === 0) {
          setError('No Google Search Console properties found for this user');
          setStep('error');
          return;
        }
      } else if (response.accounts.length === 0) {
        const serviceNames: Record<string, string> = {
          google_ads: 'Google Ads',
          gbp: 'Google Business Profile',
          facebook_ads: 'Facebook Ads',
        };
        setError(`No ${serviceNames[response.service] || 'advertising'} accounts found for this user`);
        setStep('error');
        return;
      }

      // Check if all GBP accounts were auto-refreshed (existing accounts)
      if (response.service === 'gbp' && (response.auto_refreshed ?? 0) > 0 && (response.new_accounts ?? 0) === 0) {
        // All accounts already existed and were refreshed - skip selection
        setServiceType(response.service);
        setStep('success');
        setTimeout(() => {
          navigate('/gbp');
        }, 2000);
        return;
      }

      setOauthResponse(response);
      setServiceType(response.service);

      // Load clients for linking
      const clientsResponse = await api.getClients();
      setClients(clientsResponse.results);

      setStep('selecting');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to exchange authorization code');
      setStep('error');
    }
  };

  const handleLinkAccount = async () => {
    if (!selectedAccount || !selectedClient || !oauthResponse) {
      return;
    }

    try {
      setStep('linking');

      if (serviceType === 'google_ads') {
        const account = oauthResponse.accounts.find(a => a.customer_id === selectedAccount);
        if (!account) return;

        // Check if this is a manager account with a sub-account selected
        if (account.account_type === 'manager' && account.managed_accounts) {
          // For manager accounts, we need to link a sub-account
          if (!selectedSubAccount) {
            setError('Please select a sub-account to link');
            setStep('selecting');
            return;
          }

          const subAccount = account.managed_accounts.find(
            (sa: ManagedAccount) => sa.customer_id === selectedSubAccount
          );
          if (!subAccount) return;

          await api.linkGoogleAdsAccount({
            client_id: selectedClient,
            customer_id: selectedSubAccount,  // The sub-account ID
            login_customer_id: selectedAccount,  // The manager account ID
            name: subAccount.name || `Account ${selectedSubAccount}`,
            access_token: oauthResponse.access_token,
            refresh_token: oauthResponse.refresh_token || '',
            expires_in: oauthResponse.expires_in,
            currency_code: subAccount.currency_code,
            timezone: subAccount.timezone,
          });
        } else {
          // Regular client account - link directly
          await api.linkGoogleAdsAccount({
            client_id: selectedClient,
            customer_id: selectedAccount,
            name: account.account_name || account.name || `Account ${selectedAccount}`,
            access_token: oauthResponse.access_token,
            refresh_token: oauthResponse.refresh_token || '',
            expires_in: oauthResponse.expires_in,
            currency_code: account.currency_code,
            timezone: account.timezone,
          });
        }
      } else if (serviceType === 'gbp') {
        const account = oauthResponse.accounts.find(a => a.name === selectedAccount);
        if (!account) return;

        await api.linkGBPAccount({
          client_id: selectedClient,
          account_name: account.name || '',
          display_name: account.account_name || 'Business Profile Account',
          access_token: oauthResponse.access_token,
          refresh_token: oauthResponse.refresh_token || '',
          expires_in: oauthResponse.expires_in,
          account_type: account.type,
          role: account.role,
        });
      } else if (serviceType === 'gsc') {
        const site = oauthResponse.sites?.find(s => s.site_url === selectedAccount);
        if (!site) return;

        await api.linkGSCProperty({
          client_id: selectedClient,
          site_url: site.site_url,
          display_name: site.site_url.replace(/^(https?:\/\/)?(sc-domain:)?/, ''),
          permission_level: site.permission_level,
          access_token: oauthResponse.access_token,
          refresh_token: oauthResponse.refresh_token || '',
          expires_in: oauthResponse.expires_in,
        });
      } else if (serviceType === 'facebook_ads') {
        const account = oauthResponse.accounts.find(a => a.account_id === selectedAccount);
        if (!account) return;

        await api.linkFacebookAdsAccount({
          client_id: selectedClient,
          account_id: selectedAccount,
          name: account.name || `Ad Account ${selectedAccount}`,
          access_token: oauthResponse.access_token,
          token_type: oauthResponse.token_type || 'user',
          expires_in: oauthResponse.expires_in,
          currency: account.currency,
          timezone_name: account.timezone_name,
          business_id: account.business_id,
        });
      }

      setStep('success');

      // Redirect after short delay
      setTimeout(() => {
        const paths: Record<ServiceType, string> = {
          google_ads: '/google-ads',
          gbp: '/gbp',
          gsc: '/seo',
          facebook_ads: '/facebook-ads',
        };
        navigate(paths[serviceType!]);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link account');
      setStep('error');
    }
  };

  const getRetryAuthUrl = async () => {
    try {
      if (serviceType === 'google_ads') {
        const { auth_url } = await api.getGoogleAdsAuthUrl();
        window.location.href = auth_url;
      } else if (serviceType === 'gbp') {
        const { auth_url } = await api.getGBPAuthUrl();
        window.location.href = auth_url;
      } else if (serviceType === 'gsc') {
        const { auth_url } = await api.getGSCAuthUrl();
        window.location.href = auth_url;
      } else if (serviceType === 'facebook_ads') {
        const { auth_url } = await api.getFacebookAdsAuthUrl();
        window.location.href = auth_url;
      }
    } catch {
      // Handle error
    }
  };

  const serviceNames: Record<ServiceType, string> = {
    google_ads: 'Google Ads',
    gbp: 'Google Business Profile',
    gsc: 'Google Search Console',
    facebook_ads: 'Facebook Ads',
  };
  const redirectPaths: Record<ServiceType, string> = {
    google_ads: '/google-ads',
    gbp: '/gbp',
    gsc: '/seo',
    facebook_ads: '/facebook-ads',
  };

  const serviceName = serviceType ? serviceNames[serviceType] : 'Google';
  const redirectPath = serviceType ? redirectPaths[serviceType] : '/';

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Exchanging Code */}
        {step === 'exchanging' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Connecting to {serviceName || 'Google'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">Exchanging authorization code...</p>
          </div>
        )}

        {/* Selecting Account */}
        {step === 'selecting' && oauthResponse && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Link2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Link {serviceName} Account
              </h2>
              <p className="text-gray-600 dark:text-gray-400">Select an account and link it to a client</p>
            </div>

            <div className="space-y-4">
              {/* Account Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {serviceType === 'gsc' ? 'Search Console Property' : `${serviceName} Account`}
                </label>
                <select
                  value={selectedAccount}
                  onChange={(e) => {
                    setSelectedAccount(e.target.value);
                    setSelectedSubAccount('');  // Reset sub-account when account changes
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">{serviceType === 'gsc' ? 'Select a property' : 'Select an account'}</option>
                  {serviceType === 'gsc' ? (
                    oauthResponse.sites?.map((site) => (
                      <option key={site.site_url} value={site.site_url}>
                        {site.site_url} ({site.permission_level})
                      </option>
                    ))
                  ) : (
                    oauthResponse.accounts.map((account) => {
                      // Use appropriate key based on service type
                      let key: string;
                      let label: string;
                      if (serviceType === 'google_ads') {
                        key = account.customer_id || '';
                        const isManager = account.account_type === 'manager';
                        label = `${isManager ? '🏢 ' : ''}${account.name || account.account_name} (${account.customer_id})${isManager ? ' - Manager Account' : ''}`;
                      } else if (serviceType === 'facebook_ads') {
                        key = account.account_id || '';
                        label = `${account.name} (${account.account_id})`;
                      } else {
                        key = account.name || '';
                        label = `${account.account_name || account.name} (${account.type})`;
                      }
                      return (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>

              {/* Sub-Account Selection for Manager/MCC Accounts */}
              {serviceType === 'google_ads' && selectedAccount && (() => {
                const account = oauthResponse.accounts.find(a => a.customer_id === selectedAccount);
                if (account?.account_type === 'manager' && account.managed_accounts && account.managed_accounts.length > 0) {
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          <span>Select Client Account</span>
                        </div>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        This is a Manager Account (MCC). Select a client account to link:
                      </p>
                      <select
                        value={selectedSubAccount}
                        onChange={(e) => setSelectedSubAccount(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select a client account</option>
                        {account.managed_accounts.map((subAccount: ManagedAccount) => (
                          <option key={subAccount.customer_id} value={subAccount.customer_id}>
                            {subAccount.name} ({subAccount.customer_id})
                            {subAccount.is_test_account ? ' - Test Account' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Client Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Link to Client
                </label>
                <select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select a client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name} - {client.company}
                    </option>
                  ))}
                </select>
              </div>

              {/* Selected Account Details */}
              {selectedAccount && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  {(() => {
                    if (serviceType === 'gsc') {
                      const site = oauthResponse.sites?.find(s => s.site_url === selectedAccount);
                      if (!site) return null;
                      return (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Site URL</span>
                            <span className="text-gray-900 dark:text-white font-medium truncate max-w-xs">{site.site_url}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Permission Level</span>
                            <span className="text-gray-900 dark:text-white font-medium">{site.permission_level}</span>
                          </div>
                        </div>
                      );
                    }

                    const account = oauthResponse.accounts.find(a =>
                      serviceType === 'google_ads' ? a.customer_id === selectedAccount : a.name === selectedAccount
                    );
                    if (!account) return null;

                    if (serviceType === 'google_ads') {
                      // For manager accounts, show sub-account details if selected
                      if (account.account_type === 'manager' && selectedSubAccount && account.managed_accounts) {
                        const subAccount = account.managed_accounts.find(
                          (sa: ManagedAccount) => sa.customer_id === selectedSubAccount
                        );
                        if (subAccount) {
                          return (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                                <span className="text-gray-600 dark:text-gray-400">Manager Account</span>
                                <span className="text-gray-900 dark:text-white font-medium">{account.name || account.account_name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Client Account</span>
                                <span className="text-gray-900 dark:text-white font-medium">{subAccount.name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Customer ID</span>
                                <span className="text-gray-900 dark:text-white font-medium">{subAccount.customer_id}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Currency</span>
                                <span className="text-gray-900 dark:text-white font-medium">{subAccount.currency_code || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Timezone</span>
                                <span className="text-gray-900 dark:text-white font-medium">{subAccount.timezone || '-'}</span>
                              </div>
                              {subAccount.is_test_account && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">Type</span>
                                  <span className="text-yellow-600 dark:text-yellow-400 font-medium">Test Account</span>
                                </div>
                              )}
                            </div>
                          );
                        }
                      } else if (account.account_type === 'manager') {
                        // Manager account without sub-account selected
                        return (
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2 mb-2 text-blue-600 dark:text-blue-400">
                              <Building2 className="w-4 h-4" />
                              <span className="font-medium">Manager Account (MCC)</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Customer ID</span>
                              <span className="text-gray-900 dark:text-white font-medium">{account.customer_id}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Managed Accounts</span>
                              <span className="text-gray-900 dark:text-white font-medium">{account.managed_accounts?.length || 0}</span>
                            </div>
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                              ⚠️ Please select a client account above to link
                            </p>
                          </div>
                        );
                      }

                      // Regular client account
                      return (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Customer ID</span>
                            <span className="text-gray-900 dark:text-white font-medium">{account.customer_id}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Currency</span>
                            <span className="text-gray-900 dark:text-white font-medium">{account.currency_code || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Timezone</span>
                            <span className="text-gray-900 dark:text-white font-medium">{account.timezone || '-'}</span>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Account Name</span>
                            <span className="text-gray-900 dark:text-white font-medium">{account.account_name || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Account Type</span>
                            <span className="text-gray-900 dark:text-white font-medium">{account.type || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Role</span>
                            <span className="text-gray-900 dark:text-white font-medium">{account.role || '-'}</span>
                          </div>
                        </div>
                      );
                    }
                  })()}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => navigate(redirectPath)}
                  className="flex-1 px-4 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLinkAccount}
                  disabled={(() => {
                    if (!selectedAccount || !selectedClient) return true;
                    // For manager accounts, require sub-account selection
                    if (serviceType === 'google_ads') {
                      const account = oauthResponse.accounts.find(a => a.customer_id === selectedAccount);
                      if (account?.account_type === 'manager' && !selectedSubAccount) return true;
                    }
                    return false;
                  })()}
                  className="flex-1 px-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-black disabled:text-gray-500 dark:disabled:text-gray-400 rounded-lg transition-colors font-medium"
                >
                  Link Account
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Linking */}
        {step === 'linking' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Linking Account</h2>
            <p className="text-gray-600 dark:text-gray-400">Please wait...</p>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Account Linked!</h2>
            <p className="text-gray-600 dark:text-gray-400">Redirecting to {serviceName} dashboard...</p>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Connection Failed</h2>
            <p className="text-red-600 dark:text-red-400 mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate(redirectPath)}
                className="px-6 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Go Back
              </button>
              {serviceType && (
                <button
                  onClick={getRetryAuthUrl}
                  className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-black rounded-lg transition-colors font-medium"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleCallback;
